/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import UserContactsApi from "../../../../api/UserContactsApi";
import { getGeneralError } from "../../../../util/helperFunctions";
import EmailConfirmationInfoBase from "../../confirmation/EmailConfirmationInfoBase";
import { strings } from "../../../../common/Strings/Strings";
import { EmailResponse } from "../../../../models/contact/EmailResponse";
import Modal from "../../../Modal/Modal";

interface Props {
  email: EmailResponse | null;
  show: boolean;
  onHide: () => void;
}
const ResendEmailConfirmationModal: React.FC<Props> = ({
  email,
  show,
  onHide,
}: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);

  useEffect(() => {
    const resetStateAndErrors = () => {
      setError(null);
      setResendLoading(false);
      setResendSuccess(false);
    };

    if (show) {
      resetStateAndErrors();
    }
  }, [show]);

  const resendEmailConfirmationCode = async () => {
    if (!(email && email.id)) {
      return;
    }

    setError(null);
    setResendLoading(true);
    setResendSuccess(false);

    try {
      await UserContactsApi.sendUserEmailConfirmation(email.id);
      setResendSuccess(true);
    } catch (err) {
      setError(await getGeneralError(err));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Modal handleClose={onHide} show={show}>
      <Modal.Header title={strings.emailNotConfirmed} />
      <Modal.Body>
        {email ? (
          <EmailConfirmationInfoBase
            email={email.value}
            error={error}
            loading={resendLoading}
            resend={resendEmailConfirmationCode}
            success={resendSuccess}
          />
        ) : (
          <></>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ResendEmailConfirmationModal;
